import * as React from 'react';
import { AlertTriangle, Eye, FileText } from 'lucide-react';
import { SourceCite } from '@/components/ui/citation-chip';
import { StatusChip } from '@/components/ui/status-chip';
import {
  Quote,
  WorkspaceButton,
  linkishClass,
  workspaceInputClass,
} from '@/components/ui/workspace';
import { cx } from '@/lib/utils/cn';
import type {
  EvidenceCitation,
  EvidenceSource,
  QuotedEvidence,
} from '@/lib/api/discovery-findings';
import { DocBadge, PersonAvatar } from '../coverage-bits';
import { withdrawnLine } from './findings-logic';

/**
 * The pieces every Findings screen shares: the citation of a piece of
 * evidence, who it is from, a quote row with its withdraw action, and the
 * source chips. Mirrors `citeOf`, `srcCell`, `.said-row` and `.src-chip`
 * in `docs/discovery/prototype/discovery.js` / `.css`.
 */

/** Where an item came from, as a `.cite` pill; struck through once withdrawn. */
export function EvidenceCite({
  citation,
  withdrawn = false,
  reason,
}: {
  citation: EvidenceCitation;
  withdrawn?: boolean;
  reason?: string | null;
}) {
  return (
    <SourceCite
      kind={citation.kind}
      label={citation.label}
      location={citation.location || null}
      withdrawn={withdrawn}
      title={withdrawn ? `Withdrawn: ${reason ?? ''}` : undefined}
    />
  );
}

/** Who an item is from, in words. A speaker not mapped yet has no person. */
export function sourceName(s: EvidenceSource | null): string {
  if (!s) return 'Unknown source';
  if (s.kind === 'speaker') return `${s.name || 'Speaker'} (not mapped)`;
  if (s.kind === 'interviewer') return `${s.name || 'Interviewer'} (interviewer)`;
  return s.name;
}

/**
 * The name to show beside a citation, or null when the citation already
 * says it: a questionnaire answer is located by who answered, and a
 * document's citation is the document (prototype `evPicker`).
 */
export function nameBesideCitation(c: EvidenceCitation, s: EvidenceSource | null): string | null {
  if (!s || s.kind === 'document' || c.location === s.name) return null;
  return sourceName(s);
}

/** The ledger's source cell (`.srccell`). */
export function SourceCell({ source }: { source: EvidenceSource | null }) {
  return (
    <span className="inline-flex max-w-full items-center gap-2 text-body-sm text-ink-700">
      {source?.kind === 'person' ? (
        <span aria-hidden="true" className="inline-flex">
          <PersonAvatar id={source.id} name={source.name} size={20} />
        </span>
      ) : source?.kind === 'document' ? (
        <FileText className="h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
      ) : (
        <AlertTriangle className="h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
      )}
      <span className="truncate">{sourceName(source)}</span>
    </span>
  );
}

/** "Shown on screen" — a tag, never a count (Decision 54.21). */
export function ShownOnScreenTag({ iconOnly = false }: { iconOnly?: boolean }) {
  return (
    <span
      title="Shown on screen"
      className="inline-flex items-center gap-1 text-[11px] font-medium text-posture-healthy"
    >
      <Eye className="h-3 w-3" aria-hidden="true" />
      {iconOnly ? <span className="sr-only">Shown on screen</span> : 'shown on screen'}
    </span>
  );
}

/** The typed reason a withdrawal needs (Decision 53.3), in place. */
export function WithdrawInline({
  number,
  busy,
  error,
  onWithdraw,
  onCancel,
}: {
  number: string | null;
  busy: boolean;
  error?: string | null;
  onWithdraw: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = React.useState('');
  const ok = reason.trim().length > 0;
  return (
    <form
      aria-label={`Withdraw ${number ?? 'this item'}`}
      className="mt-2 flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (ok && !busy) onWithdraw(reason.trim());
      }}
    >
      <input
        autoFocus
        aria-label={`Why ${number ?? 'this item'} is being withdrawn`}
        className={cx(workspaceInputClass, 'h-[30px] min-w-[240px] flex-1')}
        placeholder="Why is this being withdrawn?"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <WorkspaceButton type="submit" disabled={!ok || busy}>
        {busy ? 'Withdrawing…' : 'Withdraw'}
      </WorkspaceButton>
      <button type="button" className={linkishClass()} onClick={onCancel}>
        Cancel
      </button>
      {error ? (
        <p role="alert" className="w-full text-meta text-posture-critical">
          {error}
        </p>
      ) : null}
    </form>
  );
}

/**
 * One thing someone said (`.said-row`): the quote, who said it and where,
 * its number and Withdraw. A withdrawn item stays in place, struck
 * through, with its reason (Decision 53.3).
 */
export function SaidRow({
  item,
  highlighted = false,
  compact = false,
  withdrawing = false,
  busy = false,
  error,
  onStartWithdraw,
  onWithdraw,
  onCancelWithdraw,
}: {
  item: QuotedEvidence;
  highlighted?: boolean;
  /** Inside a pain point: smaller, no number, no actions. */
  compact?: boolean;
  withdrawing?: boolean;
  busy?: boolean;
  error?: string | null;
  onStartWithdraw?: () => void;
  onWithdraw?: (reason: string) => void;
  onCancelWithdraw?: () => void;
}) {
  const gone = item.status === 'withdrawn';
  return (
    <div
      id={`evidence-${item.evidence_id}`}
      className={cx(
        compact ? 'py-2' : 'border-b border-ink-100 px-4 py-3 last:border-b-0',
        highlighted && 'bg-accent-amber-50 shadow-[inset_3px_0_0_var(--accent-amber-600)]',
      )}
    >
      <Quote size={compact ? 'sm' : 'md'} struck={gone}>
        {item.text}
      </Quote>
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-meta text-ink-500">
        <span>— {sourceName(item.source)} ·</span>
        <EvidenceCite citation={item.citation} withdrawn={gone} reason={item.withdraw_reason} />
        {item.shown_on_screen && !gone ? <ShownOnScreenTag /> : null}
        {gone ? (
          <StatusChip tone="frozen" size="sm">
            Withdrawn
          </StatusChip>
        ) : null}
        {!compact ? (
          <>
            <span className="flex-1" />
            {item.number ? (
              <span className="font-mono text-meta font-medium text-ink-700">{item.number}</span>
            ) : null}
            {!gone && onStartWithdraw && !withdrawing ? (
              <button type="button" className={linkishClass()} onClick={onStartWithdraw}>
                Withdraw
              </button>
            ) : null}
          </>
        ) : null}
      </div>
      {gone && !compact ? (
        <p className="mt-1 text-meta text-ink-500">{withdrawnLine(item)}</p>
      ) : null}
      {withdrawing && onWithdraw && onCancelWithdraw ? (
        <WithdrawInline
          number={item.number}
          busy={busy}
          error={error}
          onWithdraw={onWithdraw}
          onCancel={onCancelWithdraw}
        />
      ) : null}
    </div>
  );
}

/**
 * A source of the area (`.src-chip`): green when it counts, dashed while
 * only proposals stand behind it. Pressing it shows only what that
 * source said; pressing it again shows everyone.
 */
export function SourceChip({
  source,
  on,
  onToggle,
}: {
  source: {
    key: string;
    kind: 'person' | 'document';
    id: string;
    name: string;
    counted: boolean;
    confirmed_count: number;
  };
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onToggle}
      title={source.counted ? 'Counts as a source' : 'Not counted until confirmed'}
      className={cx(
        'inline-flex h-7 items-center gap-1.5 rounded-full border pl-1 pr-2.5 text-meta font-medium',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
        on
          ? 'border-ink-900 bg-ink-900 text-paper-0'
          : source.counted
            ? 'border-posture-healthy bg-paper-0 text-ink-900'
            : 'border-dashed border-ink-200 bg-paper-0 text-ink-500',
      )}
    >
      <span aria-hidden="true" className="inline-flex">
        {source.kind === 'document' ? (
          <DocBadge name={source.name} />
        ) : (
          <PersonAvatar id={source.id} name={source.name} size={20} />
        )}
      </span>
      <span>{source.name}</span>
      {source.counted ? (
        <span className={cx('font-mono text-[11px]', on ? 'text-ink-200' : 'text-ink-500')}>
          {source.confirmed_count}
        </span>
      ) : (
        <span className={cx('text-meta', on ? 'text-ink-200' : 'text-ink-500')}>waiting</span>
      )}
    </button>
  );
}
